import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faSave, faRotate, faSliders, faCartShopping, faWallet, faMoneyBill, faVault, faCoins,
} from '@fortawesome/free-solid-svg-icons';
import { UiToggle } from '../ui';
import P2pSettingsCard from './P2pSettingsCard';

/* ── Config ───────────────────────────────────────────────────────
 * Each financial subsystem is one config object → one FinanceModuleCard.
 * Gradients use semantic theme tokens (see DESIGN_RULES → Gradient Tokens),
 * so they follow the customer's theme instead of hardcoded colors.
 */

type FieldUnit = 'baht' | 'percent' | 'usdt' | 'minutes';

interface FieldDef {
  key: string;
  label: string;
  hint?: string;
  unit: FieldUnit;
  fallback: string;
}

interface ModuleDef {
  id: string;
  title: string;
  subtitle: string;
  icon: IconDefinition;
  gradient: string;
  /** key of the enable/disable setting — omit for fee-only modules */
  toggleKey?: string;
  toggleLabel?: string;
  /** multiple enable/disable toggles in one card (e.g. Settlement THB / USDT) — takes precedence over toggleKey */
  toggles?: { key: string; label: string }[];
  fields: FieldDef[];
}

const MODULES: ModuleDef[] = [
  {
    id: 'order',
    title: 'Order (ฝาก)',
    subtitle: 'รับชำระเงินจากลูกค้า',
    icon: faCartShopping,
    gradient: 'var(--theme-gradient-success)',
    toggleKey: 'order_enabled',
    toggleLabel: 'รับ Order',
    fields: [
      { key: 'order_minimum_amount', label: 'ยอดขั้นต่ำ', hint: '0 = ไม่จำกัด', unit: 'baht', fallback: '0' },
      // ⭐ คุมทั้ง TTL ของ slip order + เพดานอายุสลิปผ่าน API (backend clamp 5–180 — min/max ตรงนี้เป็นแค่ soft validation)
      { key: 'slip_order_expiry_minutes', label: 'อายุ Slip Order', hint: 'เวลาหมดอายุ order + เพดานอายุสลิปผ่าน API', unit: 'minutes', fallback: '30' },
    ],
  },
  {
    id: 'payout',
    title: 'Payout (ถอน)',
    subtitle: 'โอนเงินออกให้ปลายทาง',
    icon: faWallet,
    gradient: 'var(--theme-accent-gradient)',
    toggleKey: 'payout_enabled',
    toggleLabel: 'Payout',
    fields: [
      { key: 'min_payout_amount', label: 'ยอดขั้นต่ำ', unit: 'baht', fallback: '300' },
    ],
  },
  {
    id: 'settlement',
    title: 'Settlement (แจ้งถอน)',
    subtitle: 'ถอนยอดเข้าบัญชี / กระเป๋า',
    icon: faMoneyBill,
    gradient: 'var(--theme-gradient-info)',
    toggles: [
      { key: 'settlement_enabled', label: 'บาท (THB)' },
      { key: 'settlement_enabled_usdt', label: 'USDT' },
    ],
    fields: [
      { key: 'min_settlement_amount', label: 'ยอดขั้นต่ำ', unit: 'baht', fallback: '300' },
      { key: 'settlement_fee_rate', label: 'ค่าธรรมเนียม', hint: 'ใช้กับ Settlement USDT และ Unfreeze แบบสร้าง Settlement', unit: 'percent', fallback: '5' },
    ],
  },
  {
    id: 'reseller_usdt',
    title: 'Reseller USDT Withdrawal',
    subtitle: 'ค่าธรรมเนียมถอน USDT ของ Reseller',
    icon: faCoins,
    gradient: 'var(--theme-gradient-warning)',
    fields: [
      { key: 'usdt_withdrawal_fee_rate', label: 'ค่าธรรมเนียม', hint: 'หักก่อนแปลง USDT', unit: 'percent', fallback: '5' },
    ],
  },
  {
    id: 'safe_wallet',
    title: 'Safe Wallet (ตู้เซฟ)',
    subtitle: 'ฝากเงินเข้า / ถอนออก Safe Wallet',
    icon: faVault,
    gradient: 'var(--theme-secondary-gradient)',
    fields: [
      { key: 'safe_wallet_min_settlement_usdt', label: 'ยอดถอนขั้นต่ำ', hint: 'เบิก USDT ออก', unit: 'usdt', fallback: '10' },
      { key: 'safe_wallet_deposit_fee_rate', label: 'ค่าธรรมเนียมโอนเข้า', hint: 'Main → Safe', unit: 'percent', fallback: '5' },
      { key: 'safe_wallet_withdraw_fee_rate', label: 'ค่าธรรมเนียมถอนออก', hint: 'Safe → Main', unit: 'percent', fallback: '2.5' },
    ],
  },
];

const UNIT_PROPS: Record<FieldUnit, { step: string; min: string; max?: string }> = {
  baht: { step: '1', min: '0' },
  percent: { step: '0.1', min: '0', max: '100' },
  usdt: { step: '0.01', min: '0' },
  minutes: { step: '1', min: '5', max: '180' },
};

interface FinanceControlSectionProps {
  editValues: Record<string, any>;
  setEditValues: React.Dispatch<React.SetStateAction<any>>;
  saving: string | null;
  /** Persist a single setting; valueOverride lets the toggle save its new value immediately */
  onSave: (key: string, valueOverride?: string) => void;
}

/* ── Inline save button (square, theme-primary) ─────────────────── */
const SaveButton: React.FC<{ onClick: () => void; busy: boolean }> = ({ onClick, busy }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className="w-9 h-9 rounded-xl text-white flex items-center justify-center transition-all hover:shadow-lg disabled:opacity-50 flex-shrink-0"
    style={{ background: 'var(--theme-primary-gradient)' }}
  >
    <FontAwesomeIcon icon={busy ? faRotate : faSave} className={`text-xs ${busy ? 'animate-spin' : ''}`} />
  </button>
);

/* ── One numeric setting row (label + input + save) ─────────────── */
const SettingField: React.FC<{
  field: FieldDef;
  value: string;
  saving: boolean;
  onChange: (v: string) => void;
  onSave: () => void;
}> = ({ field, value, saving, onChange, onSave }) => {
  const u = UNIT_PROPS[field.unit];
  return (
    <div className="bg-gray-50 dark:bg-slate-700/40 rounded-xl p-3 ring-1 ring-inset ring-gray-100 dark:ring-slate-700">
      <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 mb-2">
        {field.label}
        {field.hint && <span className="text-gray-400 dark:text-slate-500 font-normal"> ({field.hint})</span>}
      </p>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          {field.unit === 'baht' && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs font-bold pointer-events-none">฿</span>
          )}
          <input
            type="number"
            min={u.min}
            max={u.max}
            step={u.step}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full py-2 rounded-lg text-sm font-medium text-right border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 ${field.unit === 'baht' ? 'pl-7 pr-3' : field.unit === 'usdt' || field.unit === 'minutes' ? 'pl-3 pr-14' : 'pl-3 pr-8'}`}
          />
          {field.unit === 'percent' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs font-bold pointer-events-none">%</span>
          )}
          {field.unit === 'usdt' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs font-bold pointer-events-none">USDT</span>
          )}
          {field.unit === 'minutes' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs font-bold pointer-events-none">นาที</span>
          )}
        </div>
        <SaveButton onClick={onSave} busy={saving} />
      </div>
    </div>
  );
};

/* ── One subsystem card (gradient header band + body) ───────────── */
const FinanceModuleCard: React.FC<{
  module: ModuleDef;
  editValues: Record<string, any>;
  setEditValues: FinanceControlSectionProps['setEditValues'];
  saving: string | null;
  onSave: FinanceControlSectionProps['onSave'];
}> = ({ module, editValues, setEditValues, saving, onSave }) => {
  // Normalize single toggleKey and multi-toggle `toggles` into one list
  const toggleList = module.toggles
    ? module.toggles
    : module.toggleKey
      ? [{ key: module.toggleKey, label: module.toggleLabel ?? '' }]
      : [];
  const hasToggle = toggleList.length > 0;
  // Header pill: "เปิด" if any toggle is on
  const isOn = hasToggle ? toggleList.some((tg) => editValues[tg.key] === 'true') : undefined;
  const isMulti = !!module.toggles;

  return (
    <div className="relative overflow-hidden rounded-3xl shadow-lg ring-1 ring-gray-100 dark:ring-slate-700 bg-white dark:bg-slate-800 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
      {/* Gradient header band */}
      <div className="relative overflow-hidden px-5 py-4 text-white" style={{ background: module.gradient }}>
        <div className="pointer-events-none absolute -top-10 -right-8 h-28 w-28 rounded-full bg-white/20 blur-2xl" />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur flex items-center justify-center text-lg shadow-inner shrink-0">
            <FontAwesomeIcon icon={module.icon} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-[15px] leading-tight truncate">{module.title}</h3>
            <p className="text-[11px] text-white/70 truncate">{module.subtitle}</p>
          </div>
          {hasToggle && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/15 ring-1 ring-white/25 backdrop-blur shrink-0">
              {isOn ? 'เปิด' : 'ปิด'}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Enable toggle row(s) (auto-saves on change) */}
        {toggleList.map((tg) => {
          const on = editValues[tg.key] === 'true';
          return (
            <div key={tg.key} className="bg-gray-50 dark:bg-slate-700/40 rounded-xl p-3 ring-1 ring-inset ring-gray-100 dark:ring-slate-700 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-600 dark:text-slate-300">
                  {isMulti ? tg.label : 'สถานะระบบ'}
                </p>
                <p className={`text-[11px] font-bold ${on ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-slate-500'}`}>
                  {isMulti ? (on ? 'เปิด' : 'ปิด') : (on ? `เปิด${tg.label}` : `ปิด${tg.label}`)}
                </p>
              </div>
              <UiToggle
                checked={on}
                disabled={saving === tg.key}
                ariaLabel={`เปิด/ปิด ${module.title}${tg.label ? ` ${tg.label}` : ''}`}
                onChange={(next) => {
                  const value = next ? 'true' : 'false';
                  setEditValues((prev: any) => ({ ...prev, [tg.key]: value }));
                  onSave(tg.key, value);
                }}
              />
            </div>
          );
        })}

        {module.fields.map((field) => (
          <SettingField
            key={field.key}
            field={field}
            value={editValues[field.key] ?? field.fallback}
            saving={saving === field.key}
            onChange={(v) => setEditValues((prev: any) => ({ ...prev, [field.key]: v }))}
            onSave={() => onSave(field.key)}
          />
        ))}
      </div>
    </div>
  );
};

/* ── Status overview pills ──────────────────────────────────────── */
const STATUS_PILLS = [
  { key: 'order_enabled', label: 'Order', icon: faCartShopping, dot: 'bg-emerald-400', activeBg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800', activeText: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'payout_enabled', label: 'Payout', icon: faWallet, dot: 'bg-indigo-400', activeBg: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800', activeText: 'text-indigo-600 dark:text-indigo-400' },
  { key: 'settlement_enabled', label: 'Settle ฿', icon: faMoneyBill, dot: 'bg-sky-400', activeBg: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800', activeText: 'text-sky-600 dark:text-sky-400' },
  { key: 'settlement_enabled_usdt', label: 'Settle USDT', icon: faCoins, dot: 'bg-amber-400', activeBg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800', activeText: 'text-amber-600 dark:text-amber-400' },
];

const FinanceControlSection: React.FC<FinanceControlSectionProps> = ({
  editValues, setEditValues, saving, onSave,
}) => (
  <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
    {/* Header */}
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg" style={{ background: 'var(--theme-primary-gradient)' }}>
        <FontAwesomeIcon icon={faSliders} />
      </div>
      <div>
        <h2 className="font-bold text-gray-800 dark:text-slate-100">การควบคุมระบบการเงิน</h2>
        <p className="text-xs text-gray-400 dark:text-slate-500">จัดการสถานะและขั้นต่ำของ Order · Payout · Settlement</p>
      </div>
    </div>

    {/* Status overview */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6">
      {STATUS_PILLS.map(({ key, label, icon, dot, activeBg, activeText }) => {
        const isOn = editValues[key] === 'true';
        return (
          <div key={key} className={`rounded-xl border px-2 sm:px-4 py-2.5 sm:py-3 flex items-center gap-1.5 sm:gap-3 transition-all ${isOn ? activeBg : 'bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600'}`}>
            <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 ${isOn ? dot : 'bg-gray-300 dark:bg-slate-500'}`} />
            <div className="min-w-0">
              <p className={`text-[10px] sm:text-xs font-bold truncate ${isOn ? 'text-gray-700 dark:text-slate-200' : 'text-gray-400 dark:text-slate-500'}`}>{label}</p>
              <p className={`text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider ${isOn ? activeText : 'text-gray-400 dark:text-slate-500'}`}>{isOn ? 'เปิด' : 'ปิด'}</p>
            </div>
            <FontAwesomeIcon icon={icon} className={`ml-auto text-xs sm:text-sm flex-shrink-0 ${isOn ? 'text-gray-400 dark:text-slate-500' : 'text-gray-300 dark:text-slate-600'}`} />
          </div>
        );
      })}
    </div>

    {/* Module cards */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {MODULES.map((module) => (
        <FinanceModuleCard
          key={module.id}
          module={module}
          editValues={editValues}
          setEditValues={setEditValues}
          saving={saving}
          onSave={onSave}
        />
      ))}
      {/* 💧 P2P settings (บัตรพิเศษ — มี toggle 2 ตัว + เลือกบัญชี) */}
      <P2pSettingsCard
        editValues={editValues}
        setEditValues={setEditValues}
        saving={saving}
        onSave={onSave}
      />
    </div>
  </div>
);

export default FinanceControlSection;
